import React from "react";
import { FullDivider } from "./SubPage";

/**
 * Terms of Service for the Skyline iOS app (building classification).
 * Self-contained, English-only, app-specific, and deliberately separate from the
 * agency's German site content. Linked from inside the app (registration screen
 * and Settings) and referenced from App Store Connect. Content is hardcoded
 * (no i18n) so the URL renders identically for App Review regardless of locale.
 * Structure mirrors SkylinePrivacy so the two pages read as one set.
 */

type Section = {
  number: string;
  title: string;
  body?: string[];
  list?: string[];
  footer?: string[];
};

const LAST_UPDATED = "October 6, 2026";

const SECTIONS: Section[] = [
  {
    number: "1",
    title: "Agreement to these terms",
    body: [
      "These Terms of Service (“Terms”) are a binding agreement between you and 0-AI UG (haftungsbeschränkt), Neckartalstraße 127, 70376 Stuttgart, Germany (“Skyline”, “we”, “us”). They govern your use of the Skyline iOS app and the services behind it (together, “the Service”).",
      "These Terms govern use of the Service. The app asks you to accept its community guidelines and terms for account features. Public browsing is available without enabling optional app analytics or OpenAI safety moderation. The Privacy Policy explains how we handle personal data; it is not a blanket consent to processing. Those optional permissions are requested separately, and accepting these Terms does not grant them.",
      "The in-app community guidelines summarize the rules. If a summary differs from these full Terms, these Terms govern, subject always to mandatory law. In particular, the notice, review, and liability provisions below continue to apply even if an in-app summary uses shorter wording.",
    ],
  },
  {
    number: "2",
    title: "Who we are",
    body: [
      "Skyline is a game and community for discovering, photographing, and collecting the buildings around you. When you capture a building with the in-app camera, we check the selected building and capture evidence, add a verified visit to your collection, and let you share sightings, save buildings, like sightings, and help improve the map with other users.",
      "The Privacy Policy explains what data we collect and how we handle it. You can read it at https://cero-ai.com/skyline/privacy.",
    ],
  },
  {
    number: "3",
    title: "Eligibility",
    body: [
      "The current app requires you to confirm that you are at least 16, and you must have the legal capacity or other authorization required to enter this agreement. The age confirmation and contractual capacity are separate requirements. Skyline is not directed to children under 16.",
      "By using the Service, you represent that you meet these requirements and that the information you give us is accurate.",
    ],
  },
  {
    number: "4",
    title: "Your account",
    body: [
      "To use most features you need an account. You can register with an email address and password, or with Sign in with Apple. You are responsible for keeping your credentials confidential and for everything that happens under your account.",
    ],
    list: [
      "Provide accurate information and keep it up to date.",
      "Do not share your account or let anyone else use it.",
      "Do not create an account for anyone other than yourself, or use another person’s account without permission.",
      "Tell us promptly at contact@cero-ai.com if you believe your account has been compromised.",
    ],
  },
  {
    number: "5",
    title: "Collecting buildings, location, and photos",
    body: [
      "A core part of the Service is capturing photos of real buildings at your location with the in-app camera. To verify a collection, the app uses your GPS position, compass heading, timing, and camera metadata and the selected building’s location. With your separate permission, capture evidence is sent to TypeSafe AI, Inc. for a credibility check; photo pixels are not sent to TypeSafe. The Privacy Policy explains this processing. Please use the Service responsibly and lawfully in the physical world:",
    ],
    list: [
      "Obey all laws and respect private property. Do not enter restricted areas, trespass, or ignore posted signs or instructions in order to photograph a building.",
      "Stay aware of your surroundings. Do not use the app while driving, and do not put yourself or others at risk to capture a photo.",
      "Only capture and upload photos you are allowed to take and share, and that do not focus on identifiable people without their consent.",
      "Do not spoof, falsify, or manipulate your location, heading, timestamps, or photos to defeat the app’s verification or to game rankings and collections.",
    ],
    footer: [
      "You are responsible for your conduct while using the Service in the real world. Our responsibility for harm is governed by sections 12 and 13 and applicable law; this section does not exclude liability that cannot lawfully be excluded.",
      "Other signed-in users who are not blocked can access your collected buildings, collection photos, cities and countries, and a journey containing building locations and collection dates. Collections are not a private location diary. Consider that visibility before collecting or sharing content. The Privacy Policy explains the distinction between this visible activity and device capture data used for verification.",
    ],
  },
  {
    number: "6",
    title: "Your content and the license you grant us",
    body: [
      "“Your Content” means anything you submit through the Service: photos you capture or upload, buildings you collect, sightings and likes, building submissions or edits, your profile information, and reports you file. You keep ownership of Your Content.",
      "To operate the Service, you grant us a worldwide, non-exclusive, royalty-free license to host, store, reproduce, and adapt Your Content where necessary to provide the features you use, including generating processed display images and thumbnails. For content shared through community features, this also includes publishing and displaying it in those features. Reports and other non-public account information are processed only for their relevant service, safety, support, or legal purposes; this license does not authorize publishing them.",
      "This license is limited to operating the Service and does not grant a separate right to use Your Content in advertising or external promotional campaigns. It lasts only while needed to provide the relevant feature, except for shared factual building records without direct account attribution, backup copies retained for recovery, and records lawfully retained for legal purposes as explained in the Privacy Policy. Retaining a backup does not extend permission to publish or promote content you deleted. Your data-protection rights are unaffected.",
      "You represent that you have the rights necessary to submit Your Content and to grant this license, and that Your Content does not infringe anyone’s rights or break any law.",
    ],
  },
  {
    number: "7",
    title: "Acceptable use",
    body: [
      "There is no tolerance for objectionable content or abusive behavior on Skyline. Posting such content, or abusing other users, may result in the immediate removal of that content and the ejection of your account from the Service.",
      "When using the Service, you agree that you will not:",
    ],
    list: [
      "Break the law, infringe intellectual property or privacy rights, or help anyone else do so.",
      "Post content that is unlawful, hateful, harassing, threatening, defamatory, sexually explicit, or otherwise harmful, or that impersonates another person.",
      "Upload photos that are not of buildings, are irrelevant, misleading, or intended to deceive the matching system or other users.",
      "Cheat, including spoofing location or heading, automating collection, using bots or scrapers, or artificially inflating collections, votes, or rankings.",
      "Interfere with, overload, probe, or attempt to gain unauthorized access to the Service, other accounts, or our systems.",
      "Reverse engineer, decompile, or attempt to extract the source code or models of the Service, except to the extent this restriction is prohibited by law.",
      "Use the Service for commercial exploitation, advertising, or spam without our written permission.",
    ],
  },
  {
    number: "8",
    title: "Reporting and moderation",
    body: [
      "Accounts, user-generated photos, and sightings can be reported from their in-app action menus. We review reports promptly and may remove or restrict content that violates these Terms and take action against users responsible for serious or repeated violations.",
      "You can block another user from their profile. Blocking immediately hides that user’s profile, photos, and sightings from you, hides yours from them, prevents further interaction between the accounts, and notifies us so we can review the blocked account.",
      "We use automated and human moderation to enforce the Acceptable Use rules. With your separate permission, OpenAI’s moderation service checks your existing public display name when you enable AI safety and capture checks, subsequently submitted profile and building-contribution text, and resized, metadata-free copies of uploaded photos. This includes photos submitted for collection verification before saving. TypeSafe separately checks the selected building and capture evidence, including location, heading, timing, and camera metadata; it does not receive photo pixels. These systems may reject a submission or collection and may make mistakes. You may withdraw this permission in Settings, or contact datenschutz@cero-ai.com if you cannot access those controls. Browsing remains available, but posting and uploads requiring these checks are unavailable without that permission.",
      "Depending on the nature, seriousness, and frequency of a violation, we may reject or remove content, reduce its visibility, issue a warning, restrict features, suspend an account, or terminate it. Where reasonably possible, we will provide the affected user with the policy ground and general reason for a restriction. We may withhold details where disclosure would undermine security, facilitate evasion, violate another person’s rights, or be prohibited by law.",
      "You may contest a moderation decision and request human review by contacting contact@cero-ai.com. Include your account email and enough information to identify the submission or decision. A human reviewer may uphold, modify, or reverse the result.",
      "We are not obligated to monitor every submission, and we are not responsible for content submitted by users to the extent permitted by law.",
    ],
  },
  {
    number: "9",
    title: "Our intellectual property",
    body: [
      "The Service itself, including the app, its design, software, models, logos, and the arrangement of building and map data, is owned by us or our licensors and is protected by intellectual property laws. Except for the rights we expressly grant you to use the Service, we reserve all rights. You may not copy, modify, distribute, sell, or lease any part of the Service.",
    ],
  },
  {
    number: "10",
    title: "Third-party services and the App Store license",
    body: [
      "The Service relies on third parties, including Apple for App Store distribution, Sign in with Apple, and MapKit; OpenAI for text and image safety moderation; TypeSafe AI, Inc. for capture-evidence verification; and the infrastructure and email providers described in our Privacy Policy. You must comply with applicable third-party terms when using the Service.",
      "These Terms govern the Skyline service and supplement, rather than replace, Apple’s Standard Licensed Application End User License Agreement (“Apple Standard EULA”), which governs the license to the iOS app unless the App Store product page expressly displays another license. The Apple Standard EULA is available at https://www.apple.com/legal/internet-services/itunes/dev/stdeula/. The following also applies because you obtain Skyline through the Apple App Store:",
    ],
    list: [
      "These Terms are between you and us only, not with Apple. Apple is not responsible for the app or its content.",
      "Your license to use the app is limited to a non-transferable license to use it on any Apple device you own or control, as permitted by the App Store Terms of Service.",
      "We, not Apple, are responsible for maintenance and support for the app. Apple has no obligation to provide maintenance or support.",
      "We are responsible for warranties and claims relating to the app to the extent required by applicable law. If the app fails to conform to an applicable warranty, you may notify Apple, and Apple may refund the purchase price (if any); to the maximum extent permitted by law, Apple has no other warranty obligation.",
      "We, not Apple, are responsible for addressing product-liability, legal, regulatory, consumer-protection, privacy, and third-party intellectual-property claims relating to the app, including the investigation, defense, settlement, and discharge of an intellectual-property claim where required by law.",
      "You represent that you are not located in a country subject to a United States government embargo or designated by that government as supporting terrorism, and that you are not listed on a United States government list of prohibited or restricted parties.",
      "Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you.",
    ],
  },
  {
    number: "11",
    title: "Suspension and termination",
    body: [
      "You can stop using the Service at any time and request account deletion under Settings → Delete Account or by emailing datenschutz@cero-ai.com. If your account is suspended, you cannot sign in, or the in-app process fails, use the email route. Suspension or termination does not remove your data-protection rights. The Privacy Policy explains deletion from the active service, queued image removal, backup and operational-record limits, and shared factual building records that may remain without direct account attribution.",
      "We may restrict or suspend access where reasonably necessary to protect users or the Service, investigate suspected abuse, comply with law, or respond proportionately to a violation of these Terms. Except where immediate action is reasonably necessary for safety, security, repeated or serious abuse, or legal compliance, we will ordinarily give notice of the reason and an opportunity to contest the decision. We may terminate the Service generally on reasonable notice where practicable.",
      "On termination, the licenses you granted us continue only as needed for content that lawfully remains in the Service or as the law requires, and the sections of these Terms that by their nature should survive will survive.",
    ],
  },
  {
    number: "12",
    title: "Service availability and statutory rights",
    body: [
      "Building matches, locations, rankings, moderation results, and other automated results may be inaccurate or incomplete. We do not promise that the Service will always be uninterrupted or error-free, but we will provide it with the care required by applicable law.",
      "Nothing in these Terms excludes or limits statutory warranty, consumer-protection, digital-content, or other rights that cannot lawfully be excluded or limited.",
    ],
  },
  {
    number: "13",
    title: "Limitation of liability",
    body: [
      "We are liable without limitation for intent and gross negligence; for injury to life, body, or health; under the German Product Liability Act; where we have expressly guaranteed a characteristic; and in every other case where liability cannot lawfully be limited.",
      "For slight negligence, we are liable only for breach of an essential contractual obligation whose performance is necessary for the agreement to function and on which you may ordinarily rely. In that case, liability is limited to the foreseeable damage typical for this agreement. Otherwise, liability for slight negligence is excluded to the extent permitted by law. These limitations also apply to our legal representatives and agents.",
    ],
  },
  {
    number: "14",
    title: "Responsibility for violations",
    body: [
      "You remain responsible under applicable law for harm caused by your culpable misuse of the Service, violation of these Terms, or infringement of another person’s rights. This section does not create liability without fault, remove any defense available to you, or require a consumer to assume liability that applicable law places on us.",
    ],
  },
  {
    number: "15",
    title: "Changes to the Service and these Terms",
    body: [
      "We may change the Service and these Terms where reasonably necessary to reflect changes in law, security requirements, technical operation, available features, or harmful practices, while taking your interests into account. We will explain material changes and provide reasonable advance notice in the app or by email where practicable. Urgent safety, security, or legal changes may take effect sooner.",
      "A change will not retroactively reduce rights you have already acquired. This October 6, 2026 revision names TypeSafe AI, Inc. as the capture-verification provider. Publishing these Terms does not itself change a recorded in-app acceptance or privacy permission. Where applicable law requires consent to a change, it will apply to you only after you consent. If you do not agree to a prospective change, you may stop using the Service and request account deletion before it takes effect.",
    ],
  },
  {
    number: "16",
    title: "Governing law and disputes",
    body: [
      "These Terms are governed by the laws of the Federal Republic of Germany, excluding its conflict-of-laws rules and the UN Convention on Contracts for the International Sale of Goods. Where you are a consumer, you also keep the protection of the mandatory laws of your country of residence, and this choice of law does not deprive you of them.",
      "The courts of our registered seat have jurisdiction to the extent permitted by law. As a consumer, you may also be able to bring proceedings in the courts of your place of residence.",
    ],
  },
  {
    number: "17",
    title: "Contact",
    body: [
      "0-AI UG (haftungsbeschränkt)\nNeckartalstraße 127\n70376 Stuttgart\nGermany",
      "Email: contact@cero-ai.com",
    ],
  },
];

function TOCLink({ number, label }: { number: string; label: string }) {
  return (
    <a
      href={`#tos-${number}`}
      onClick={(e) => {
        e.preventDefault();
        document.getElementById(`tos-${number}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className="flex items-center gap-3 text-[12px] text-grey-500 hover:text-white/70 transition-colors py-1"
    >
      <span className="font-mono text-[10px] text-grey-600 w-4 text-right">{number}</span>
      <span>{label}</span>
    </a>
  );
}

export function SkylineTerms() {
  return (
    <div className="pt-14 bg-grey-975">
      <div className="px-5 sm:px-8 md:px-[66px] py-16 md:py-24">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6 max-w-2xl leading-none">
          Skyline Terms of Service
        </h1>
        <p className="text-[12px] tracking-[0.01em] text-grey-500">Last updated: {LAST_UPDATED}</p>
      </div>
      <FullDivider />

      <div className="px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
        <p className="text-[12px] tracking-[0.01em] text-grey-500 mb-3">Contents</p>
        <div className="space-y-0.5">
          {SECTIONS.map((s) => (
            <TOCLink key={s.number} number={s.number} label={s.title} />
          ))}
        </div>
      </div>
      <FullDivider />

      <div>
        {SECTIONS.map((section) => (
          <div key={section.number} id={`tos-${section.number}`} className="px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
            <div className="flex gap-4 sm:gap-6">
              <div className="shrink-0 flex flex-col items-center">
                <span className="text-[11px] font-mono text-grey-600 mb-2">{section.number.padStart(2, "0")}</span>
                <div className="flex-1 w-px bg-white/[0.06]" />
              </div>
              <div>
                <h2 className="text-[15px] font-medium text-white mb-3">{section.title}</h2>
                <div className="text-[13px] text-grey-400 leading-[1.75] space-y-3">
                  {section.body?.map((para, i) => (
                    <p key={i} className="whitespace-pre-line">{para}</p>
                  ))}
                  {section.list && (
                    <ul className="space-y-1.5 ml-1">
                      {section.list.map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <span className="shrink-0 w-1 h-1 rounded-full bg-white/15 mt-[9px]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.footer?.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <FullDivider />
    </div>
  );
}
